import { useCallback, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useCreateIntegration } from "./useCreateIntegration";
import { useReauthenticateIntegration } from "./useReauthenticateIntegration";
import { getLoggedUserProfile } from "../../../utils/auth";
import env from "../../../utils/env";
import { useSnackbar } from "../../../components/Snackbar";
import {
  SFDC_AUTH_CALLBACK_RESPONSE,
  SFDC_AUTH_URLS,
  ACCOUNT_MATCHING_PREFERRED_COUNTRY_FIELD,
  DEFAULT_ORG_NAMES,
  POPUP_CONFIG,
} from "../consts";
import { useSalesforcePageContext } from "../contexts/useSalesforcePageContext";

interface UseSfdcAuthenticationOptions {
  isProdEnv: boolean;
  orgNames?: string[];
  reauthenticateIntegrationId?: string;
}

export const useSfdcAuthentication = ({
  isProdEnv,
  orgNames = [],
  reauthenticateIntegrationId,
}: UseSfdcAuthenticationOptions) => {
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const popupRef = useRef<Window | null>(null);
  // Add a new ref to track cleanup timers
  const cleanupTimerRef = useRef<NodeJS.Timeout>(undefined);
  const storageListenerRef = useRef<((event: StorageEvent) => void) | null>(
    null
  );
  const { refetch: refetchIntegrations, setShowBackdrop } =
    useSalesforcePageContext();

  const { createIntegration } = useCreateIntegration({
    onCompleted: async (data) => {
      if (data?.createIntegration?.result?.id) {
        await refetchIntegrations();

        showSnackbar("Salesforce integration created successfully!", "success");

        navigate(
          `/integrations/salesforce/${data.createIntegration.result.id}`,
          { replace: true }
        );

        // Keep backdrop showing briefly to ensure smooth transition
        setTimeout(() => {
          setShowBackdrop(false);
        }, 1000);
      }
    },
    onError: (error) => {
      console.error("Error creating integration:", error);
      showSnackbar("Failed to create Salesforce integration", "error");
      setShowBackdrop(false);
    },
  });

  const { reauthenticateIntegration } = useReauthenticateIntegration({
    onCompleted: async (data) => {
      if (data?.reauthenticateIntegration) {
        await refetchIntegrations();

        showSnackbar(
          "Salesforce integration reauthenticated successfully!",
          "success"
        );
      }
    },
    onError: (error) => {
      console.error("Error reauthenticating integration:", error);
      showSnackbar("Failed to reauthenticate Salesforce integration", "error");
    },
  });

  const generateUniqueName = useCallback(
    (baseName: string): string => {
      if (!orgNames.includes(baseName)) {
        return baseName;
      }

      let counter = 2;
      let uniqueName = `${baseName} ${counter}`;
      while (orgNames.includes(uniqueName)) {
        counter++;
        uniqueName = `${baseName} ${counter}`;
      }
      return uniqueName;
    },
    [orgNames]
  );

  const cleanupAuthFlow = useCallback(() => {
    // Clear any pending cleanup timers
    if (cleanupTimerRef.current) {
      clearTimeout(cleanupTimerRef.current);
      cleanupTimerRef.current = undefined;
    }
    if (popupRef.current && !popupRef.current.closed) {
      popupRef.current.close();
    }
    if (storageListenerRef.current) {
      window.removeEventListener("storage", storageListenerRef.current);
      storageListenerRef.current = null;
    }
    localStorage.removeItem(SFDC_AUTH_CALLBACK_RESPONSE);
  }, []);

  const handleAuthCallback = useCallback(
    async (storageEvent: StorageEvent) => {
      if (storageEvent.key !== SFDC_AUTH_CALLBACK_RESPONSE) return;

      const callbackResponse = storageEvent.newValue;
      if (!callbackResponse) return;

      try {
        const parsedResponse = JSON.parse(callbackResponse);
        const { authorizationCodeGrant, error } = parsedResponse;

        if (error) {
          showSnackbar(`Authentication failed: ${error}`, "error");
          cleanupAuthFlow();
          return;
        }

        if (!authorizationCodeGrant) {
          showSnackbar("No authorization code received", "error");
          cleanupAuthFlow();
          return;
        }

        const userProfile = getLoggedUserProfile();
        if (!userProfile?.email) {
          showSnackbar("User profile not found", "error");
          cleanupAuthFlow();
          return;
        }

        const { protocol, host } = window.location;
        const redirectUri = new URL(`${protocol}//${host}/auth/sfdc`);

        if (reauthenticateIntegrationId) {
          await reauthenticateIntegration({
            authorizationCodeGrant,
            redirectUri: redirectUri.href,
            integrationId: reauthenticateIntegrationId,
          });
        } else {
          const defaultName = isProdEnv
            ? DEFAULT_ORG_NAMES.production
            : DEFAULT_ORG_NAMES.sandbox;
          const uniqueName = generateUniqueName(defaultName);

          setShowBackdrop(true);

          await createIntegration({
            authorizationCodeGrant,
            redirectUri: redirectUri.href,
            name: uniqueName,
            createdByEmail: userProfile.email,
            environment: isProdEnv ? "PRODUCTION" : "SANDBOX",
            autoSync: true,
            syncAccounts: true,
            syncLeads: true,
            exportTechInstalls: true,
            exportItSpend: true,
            exportFirmographics: true,
            exportAccountScoring: true,
            exportCloudDynamics: false, // Set to false by default since UI is removed
            customHgIdMatching: false,
            useCountryLevelMatching: false,
            accountsCountryField:
              ACCOUNT_MATCHING_PREFERRED_COUNTRY_FIELD.BILLING_COUNTRY,
            localizeInstalls: true,
          });
        }

        cleanupAuthFlow();
      } catch (error) {
        console.error("Error processing auth callback:", error);
        showSnackbar("Failed to process authentication", "error");
        cleanupAuthFlow();
      }
    },
    [
      isProdEnv,
      reauthenticateIntegrationId,
      generateUniqueName,
      createIntegration,
      reauthenticateIntegration,
      showSnackbar,
      setShowBackdrop,
      cleanupAuthFlow,
    ]
  );

  const authenticate = useCallback(() => {
    try {
      const { protocol, host } = window.location;
      const redirectUri = new URL(`${protocol}//${host}/auth/sfdc`);
      const sfdcAuthenticationUrl = new URL(
        isProdEnv ? SFDC_AUTH_URLS.production : SFDC_AUTH_URLS.sandbox
      );

      // Add OAuth parameters
      const oauthParams = new URLSearchParams({
        prompt: "login",
        display: "popup",
        response_type: "code",
        client_id: env.HG_FORCE_SFDC_CLIENT_ID || "", // Allow empty client ID to be handled by the auth flow
        scope: "api refresh_token",
        redirect_uri: redirectUri.href,
      });

      sfdcAuthenticationUrl.search = oauthParams.toString();

      // Clean up any existing auth flow
      cleanupAuthFlow();

      // Set up storage event listener
      storageListenerRef.current = handleAuthCallback;
      window.addEventListener("storage", storageListenerRef.current);

      // Open popup window
      const left = (window.screen.width - POPUP_CONFIG.width) / 2;
      const top = (window.screen.height - POPUP_CONFIG.height) / 2;

      popupRef.current = window.open(
        sfdcAuthenticationUrl.href,
        "_blank",
        `popup,width=${POPUP_CONFIG.width},height=${POPUP_CONFIG.height},left=${left},top=${top}`
      );

      if (!popupRef.current) {
        showSnackbar(
          "Failed to open authentication popup. Please allow popups for this site.",
          "error"
        );
        cleanupAuthFlow();
        return;
      }

      // Monitor popup closure
      const checkClosed = setInterval(() => {
        if (popupRef.current?.closed) {
          clearInterval(checkClosed);
          // Only clean up if we haven't received a callback yet
          cleanupTimerRef.current = setTimeout(() => {
            if (storageListenerRef.current) {
              cleanupAuthFlow();
            }
          }, 1000);
        }
      }, 1000);
    } catch (error) {
      console.error("Error starting authentication:", error);
      showSnackbar("Failed to start authentication", "error");
      cleanupAuthFlow();
    }
  }, [isProdEnv, handleAuthCallback, showSnackbar, cleanupAuthFlow]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanupAuthFlow();
    };
  }, [cleanupAuthFlow]);

  return authenticate;
};
